
import { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

type ConsentStatus = "pending" | "accepted" | "rejected";

export const CookieConsent = () => {
  const [status, setStatus] = useState<ConsentStatus>("pending");
  const [isVisible, setIsVisible] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    // Check for saved consent on mount
    const savedConsent = localStorage.getItem("tracking-consent");
    
    if (savedConsent) {
      setStatus(savedConsent as ConsentStatus);
    } else {
      // Only show cookie banner after a slight delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleAccept = () => {
    setStatus("accepted");
    setIsVisible(false);
    localStorage.setItem("tracking-consent", "accepted");
    
    // Optional: track the consent acceptance
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'consent_accepted', { timestamp: new Date().toISOString() });
      }
    } catch (err) {
      console.error('Error tracking consent:', err);
    }
    
    toast({
      title: "Thank you!",
      description: "Your preferences have been saved.",
      duration: 3000
    });
  };
  
  const handleReject = () => {
    setStatus("rejected");
    setIsVisible(false);
    localStorage.setItem("tracking-consent", "rejected");
    
    toast({
      title: "Preferences updated",
      description: "Your choices have been saved.",
      duration: 3000
    });
  };
  
  if (!isVisible || status !== "pending") {
    return null; // Remove the persistent Privacy button by returning null
  }
  
  return (
    <div className={cn(
      "fixed bottom-4 left-0 right-0 mx-auto w-full max-w-lg px-4 z-50 transition-all duration-500",
      isVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
    )}>
      <div className="glass-effect p-4 rounded-lg shadow-lg border border-edgefi-purple/20">
        <div className="flex flex-col space-y-4">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-medium">Cookie Consent</h3>
            <Button 
              variant="ghost"
              size="icon"
              onClick={handleReject}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Dismiss cookie notice"
            >
              <X size={20} />
            </Button>
          </div>
          
          <p className="text-sm text-gray-600">
            We use cookies and similar technologies to help personalize content, enhance your experience, 
            and analyze our traffic. By clicking "Accept", you consent to our use of cookies.
          </p>
          
          <div className="flex items-center justify-end space-x-3 pt-2">
            <Button 
              variant="outline"
              size="sm"
              onClick={handleReject}
              className="border-gray-300 text-gray-600"
            >
              Decline
            </Button>
            <Button 
              variant="default"
              size="sm"
              onClick={handleAccept}
              className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 flex items-center space-x-1"
            >
              <Check size={16} />
              <span>Accept</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
